import React from "react";
import { Spin, Button, message, Row, Col, Alert, Collapse } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import * as Sentry from "@sentry/browser";
import { UploadFile } from "antd/lib/upload/interface";
import BaseForm from "../components/Form/BaseForm";

import {
  APIError,
  Customer,
  FormFieldConfig,
  LoanBreakdownData,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  getCompanyPetraId,
  request,
  responseHandler,
  generateDisplayErrorMessage,
  mapAPIErrorToFormError,
  filterActiveProducts,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import { SEARCH_CUSTOMER } from "../gql/customers";
import {
  GET_PROVIDERS,
  CREATE_LOAN_REQUEST,
  CHECK_LOAN_BREAKDOWN,
  GET_REQUIRED_LOAN_DOCUMENTS,
} from "../gql/loans";
import { LoanEligibilityCard } from "../components/LoanEligibilityCard";
import {
  contributionTypes,
  loanTenureMonths,
  loanTypes,
} from "../helpers/constants";
import { GET_SALES_PEOPLE } from "../gql/users";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface NewLoanProps {
  customer?: Customer;
  onSuccess?: () => void;
}

interface NewLoanState {
  isSubmitting: boolean;
  selectedProviderId?: string;
  selectedProvider?: any;
  selectedCustomer?: Customer;
  selectedSubscription?: any;
  customers: Customer[];
  isSearching: boolean;
  searchTerm: string;
  providers: SelectOptionsInterface[];
  products: SelectOptionsInterface[];
  page: number;
  rawProviders: any[];
  loanBreakdownData?: LoanBreakdownData;
  isCheckingLoanBreakdown: boolean;
  loanBreakdownError?: string;
  selectedCompanyId?: string;
  selectedProductId?: string;
  selectedLoanType?: string;
  requiredDocuments?: string[];
  isLoadingDocuments: boolean;
  ghanaCardFrontUrl?: string;
  ghanaCardBackUrl?: string;
  passportUrl?: string;
  applicationFormUrl?: string;
  threeMonthsPayslipUrl?: string;
  standingOrderUrl?: string;
  threeMonthBankStatementUrl?: string;
  threeMomoStatementUrl?: string;
  oneYearConsistentTier2ContributionUrl?: string;
  clearanceFormUrl?: string;
  settlementQuotationUrl?: string;
  sixMonthsBankStatementUrl?: string;
  sixMonthsPayslipsUrl?: string;
  employmentOfferLetterUrl?: string;
  borrowersMandateUrl?: string;
  guarantorFormsUrl?: string;
  salesPersonSearchTerm: string;
  salesPersons: User[];
  createLoanError?: string;
}

export class NewLoanRequest extends React.Component<
  NewLoanProps,
  NewLoanState
> {
  formRef = React.createRef<FormInstance>();
  documentFormRef = React.createRef<FormInstance>();

  state: NewLoanState = {
    isSubmitting: false,
    selectedProviderId: undefined,
    selectedCustomer: undefined,
    selectedSubscription: undefined,
    customers: [],
    isSearching: false,
    searchTerm: "",
    providers: [],
    products: [],
    page: 1,
    rawProviders: [],
    loanBreakdownData: undefined,
    isCheckingLoanBreakdown: false,
    loanBreakdownError: undefined,
    isLoadingDocuments: false,
    ghanaCardFrontUrl: undefined,
    ghanaCardBackUrl: undefined,
    passportUrl: undefined,
    applicationFormUrl: undefined,
    threeMonthsPayslipUrl: undefined,
    standingOrderUrl: undefined,
    threeMonthBankStatementUrl: undefined,
    threeMomoStatementUrl: undefined,
    oneYearConsistentTier2ContributionUrl: undefined,
    clearanceFormUrl: undefined,
    settlementQuotationUrl: undefined,
    sixMonthsBankStatementUrl: undefined,
    sixMonthsPayslipsUrl: undefined,
    employmentOfferLetterUrl: undefined,
    borrowersMandateUrl: undefined,
    guarantorFormsUrl: undefined,
    salesPersonSearchTerm: "",
    salesPersons: [],
  };

  componentDidMount(): void {
    this.getAllProviders();
    this.getAllSalesPerson();

    if (this.props.customer) {
      this.setState({
        selectedCustomer: this.props.customer,
        // Auto-populate Ghana Card documents from customer prop if they exist
        ghanaCardFrontUrl: this.props.customer.ghanaCardIdUrl || undefined,
        ghanaCardBackUrl: this.props.customer.ghanaCardIdBackUrl || undefined,
      });
    }
  }

  componentDidUpdate(_: NewLoanProps, prevState: NewLoanState) {
    if (
      prevState.searchTerm !== this.state.searchTerm &&
      this.state.searchTerm
    ) {
      this.triggerCustomerSearch();
    }
  }

  getAllProviders(): void {
    const { page } = this.state;
    request({
      query: GET_PROVIDERS,
      variables: {
        page: page.toString(),
        perPage: "25",
      },
    })
      .then(({ data: { data } }: any): void => {
        const providers = data.getProviders || [];

        this.setState({
          providers,
          rawProviders: providers,
        });
      })
      .catch((error) => {
        message.error("Failed to load providers");
        Sentry.captureException(error);
      });
  }

  triggerCustomerSearch = async (): Promise<void> => {
    const q = this.state.searchTerm;

    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`Customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({ customers: mutation.results });
      }
    } catch (error) {
      message.error(`Customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearching: false });
    }
  };

  handleCustomerSearch = debounce((searchTerm: string): void => {
    this.setState({ searchTerm });
  }, DEBOUNCE_TIME);

  handleCustomerSelect = (id: string): void => {
    const selectedCustomer = this.state.customers.find((c) => c.id === id);

    if (!selectedCustomer?.isGhanaCardVerified) {
      message.error(
        "Loan cannot be created for a customer with an unverified Ghana Card",
      );

      this.setState({
        selectedCustomer: undefined,
        ghanaCardFrontUrl: undefined,
        ghanaCardBackUrl: undefined,
      });

      this.formRef.current?.setFieldsValue({
        customerId: undefined,
        customerSubscriptionId: undefined,
      });

      return;
    }

    this.setState({
      selectedCustomer,
      // Auto-populate Ghana Card documents from customer if they exist
      ghanaCardFrontUrl: selectedCustomer?.ghanaCardIdUrl || undefined,
      ghanaCardBackUrl: selectedCustomer?.ghanaCardIdBackUrl || undefined,
      // selectedSubscription: undefined,
      // maxLoanAmountData: undefined,
      // loanAmountError: undefined,
    });

    this.formRef.current?.setFieldsValue({
      customerId: id,
      customerSubscriptionId: undefined,
      // loanAmountRequested: undefined,
    });
  };

  handleCompanySelect = (companyId: string): void => {
    this.setState({
      selectedCompanyId: companyId,
      loanBreakdownData: undefined,
      loanBreakdownError: undefined,
    });
  };

  handleSubscriptionSelect = (subscriptionId: string): void => {
    const currentCustomer = this.state.selectedCustomer || this.props.customer;

    if (!currentCustomer) {
      message.error("Please select a customer first");
      return;
    }

    const selectedSubscription = currentCustomer.customerSubscriptions?.find(
      (sub) => sub.id === subscriptionId,
    );

    if (!selectedSubscription) {
      message.error("Subscription not found");
      return;
    }

    this.setState({
      selectedSubscription,
      loanBreakdownData: undefined,
      loanBreakdownError: undefined,
    });

    this.formRef.current?.setFieldsValue({
      loanAmountRequested: undefined,
    });
  };

  handleProviderSelect = (id: string): void => {
    const { rawProviders } = this.state;
    const selectedProvider = rawProviders.find((p) => p.id === id);

    const products = filterActiveProducts<any>(
      selectedProvider?.activeProducts,
    );

    this.setState({
      selectedProviderId: id,
      selectedProvider,
      products,
      selectedProductId: undefined,
      requiredDocuments: undefined,
      selectedLoanType: undefined,
    });

    this.formRef.current?.setFieldsValue({
      productId: undefined,
      loanType: undefined,
    });
  };

  // Required documents now depend on the product (provider + security type are
  // derived from it server-side), so fetch them once both the product and the
  // loan type are known -- whichever is picked last triggers the load.
  handleProductSelect = (productId: string): void => {
    this.setState({ selectedProductId: productId, requiredDocuments: undefined });

    const { selectedLoanType } = this.state;
    if (selectedLoanType) {
      this.fetchRequiredDocuments(productId, selectedLoanType);
    }
  };

  handleLoanTypeSelect = (loanType: string): void => {
    this.setState({ selectedLoanType: loanType, requiredDocuments: undefined });

    const { selectedProductId } = this.state;
    if (selectedProductId) {
      this.fetchRequiredDocuments(selectedProductId, loanType);
    }
  };

  fetchRequiredDocuments = async (
    productId: string,
    loanType: string,
  ): Promise<void> => {
    this.setState({ isLoadingDocuments: true, requiredDocuments: undefined });

    try {
      const response: AxiosResponse = await request({
        query: GET_REQUIRED_LOAN_DOCUMENTS,
        variables: {
          productId,
          loanType,
        },
      });

      const documents = response.data?.data?.getRequiredLoanDocuments || [];

      // Convert document objects to camelCase field names with "Url" suffix
      // e.g., { id: "ghana_card_front", name: "Ghana Card Front" } -> "ghanaCardFrontUrl"
      const requiredDocuments = documents.map(
        (doc: { id: string; name: string; }) => {
          const camelCase = doc.id.replace(/_([a-z])/g, (_, letter: string) =>
            letter.toUpperCase(),
          );
          return `${camelCase}Url`;
        },
      );

      this.setState({
        requiredDocuments,
      });
    } catch (error) {
      message.error(`Failed to load required documents - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({
        requiredDocuments: [],
      });
    } finally {
      this.setState({ isLoadingDocuments: false });
    }
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState({ [field]: url } as any);
  };

  getAllSalesPerson = (): void => {
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: 1,
        q: this.state.salesPersonSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        salesPersons: data.salesPeople.results,
      });
    });
  };

  handleSalesPersonSearchInput = async (
    salesPersonSearchTerm: string,
  ): Promise<void> => {
    this.setState({ salesPersonSearchTerm }, () => {
      this.triggerSalesPersonDelayedSearch();
    });
  };

  triggerSalesPersonDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllSalesPerson();
  }, DEBOUNCE_TIME);

  handleDelete = (file: UploadFile): void => {
    // Find which field this file belongs to by checking the URL
    const fileUrl = file.url;
    const stateKeys = [
      "ghanaCardFrontUrl",
      "ghanaCardBackUrl",
      "passportUrl",
      "applicationFormUrl",
      "threeMonthsPayslipUrl",
      "standingOrderUrl",
      "threeMonthBankStatementUrl",
      "threeMomoStatementUrl",
      "oneYearConsistentTier2ContributionUrl",
      "clearanceFormUrl",
      "settlementQuotationUrl",
      "sixMonthsBankStatementUrl",
      "sixMonthsPayslipsUrl",
      "employmentOfferLetterUrl",
      "borrowersMandateUrl",
      "guarantorFormsUrl",
    ];

    stateKeys.forEach((key) => {
      if (this.state[key as keyof NewLoanState] === fileUrl) {
        this.setState({ [key]: undefined } as any);
      }
    });
  };

  redirectToViewLoanRequestPage(id: string): void {
    window.location.href = `/loan-requests/${id}`;
  }

  createLoanRequest = async (): Promise<void> => {
    if (!this.formRef.current || !this.documentFormRef.current) return;

    let data;

    try {
      data = await this.formRef.current.validateFields([
        "customerId",
        "companyId",
        "customerSubscriptionId",
        "providerId",
        "productId",
        "loanType",
        "loanAmountRequested",
        "loanTenureMonths",
        "loanPurpose",
        "portionOfContributionToBeUsed",
        "salesPersonId",
      ]);
    } catch (error) {
      console.error("Form validation error:", error);
      message.error(`Please fill in all required fields - ${generateDisplayErrorMessage(error)}`);
      return;
    }

    const { loanBreakdownData } = this.state;

    if (!loanBreakdownData) {
      message.warning("Please check loan eligibility first");
      return;
    }

    if (!loanBreakdownData.data.eligible_for_loan) {
      message.error("Customer is not eligible for this loan");
      return;
    }

    this.setState({ isSubmitting: true, createLoanError: undefined });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_LOAN_REQUEST,
        variables: {
          customerId: this.props.customer?.id || data.customerId,
          companyId: data.companyId,
          providerId: data.providerId,
          productId: data.productId,
          loanType: data.loanType,
          loanAmountRequested: data.loanAmountRequested,
          loanTenureMonths: parseInt(data.loanTenureMonths),
          loanPurpose: data.loanPurpose || null,
          customerSubscriptionId: data.customerSubscriptionId,
          loanDuration: data.loanDuration || null,
          portionOfContributionToBeUsed: data.portionOfContributionToBeUsed,
          source: "phoenix",
          ghanaCardFrontUrl: this.state.ghanaCardFrontUrl || null,
          ghanaCardBackUrl: this.state.ghanaCardBackUrl || null,
          passportUrl: this.state.passportUrl || null,
          applicationFormUrl: this.state.applicationFormUrl || null,
          threeMonthsPayslipUrl: this.state.threeMonthsPayslipUrl || null,
          standingOrderUrl: this.state.standingOrderUrl || null,
          threeMonthBankStatementUrl:
            this.state.threeMonthBankStatementUrl || null,
          threeMomoStatementUrl: this.state.threeMomoStatementUrl || null,
          oneYearConsistentTier2ContributionUrl:
            this.state.oneYearConsistentTier2ContributionUrl || null,
          clearanceFormUrl: this.state.clearanceFormUrl || null,
          settlementQuotationUrl: this.state.settlementQuotationUrl || null,
          sixMonthsBankStatementUrl:
            this.state.sixMonthsBankStatementUrl || null,
          sixMonthsPayslipsUrl: this.state.sixMonthsPayslipsUrl || null,
          employmentOfferLetterUrl: this.state.employmentOfferLetterUrl || null,
          borrowersMandateUrl: this.state.borrowersMandateUrl || null,
          guarantorFormsUrl: this.state.guarantorFormsUrl || null,
          salesPersonId: data.salesPersonId,
        },
      });

      const { mutation, err } = responseHandler<{
        loanRequest?: any;
        createLoanRequest?: {
          loanRequest: any;
          errors: APIError[];
        };
      }>(response, "createLoanRequest");

      const { loanRequest } = mutation?.createLoanRequest ?? mutation;

      if (loanRequest && !err) {
        message.success("Loan request created successfully!", 1.5, () => {
          this.redirectToViewLoanRequestPage(loanRequest.id);
        });
      } else if (err || loanRequest?.errors?.length) {
        const errors = err || loanRequest.errors;
        console.error("Loan request errors:", errors);
        message.error(generateDisplayErrorMessage(errors));
        this.formRef.current?.setFields(errors.map(mapAPIErrorToFormError));
        this.setState({ createLoanError: generateDisplayErrorMessage(errors) });
      } else {
        message.error("An unexpected error occurred.");
        this.setState({ createLoanError: "An unexpected error occurred." });
      }
    } catch (error) {
      console.error("Failed to create loan request:", error);
      message.error(`Failed to create loan request - ${generateDisplayErrorMessage(error)}`);
      this.setState({ createLoanError: "Failed to create loan request" });
      Sentry.captureException(error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  checkLoanBreakdown = async (): Promise<void> => {
    const currentCustomer = this.state.selectedCustomer || this.props.customer;
    const { selectedSubscription, selectedCompanyId, selectedProviderId } =
      this.state;

    if (!this.formRef.current) return;

    let formData;
    try {
      formData = await this.formRef.current.validateFields([
        "loanAmountRequested",
        "loanType",
        "portionOfContributionToBeUsed",
        "loanTenureMonths",
        "productId",
      ]);
    } catch {
      message.warning("Please fill in required fields to check eligibility");
      return;
    }

    if (!currentCustomer?.petraId) {
      message.warning("Customer Petra ID is missing");
      return;
    }

    if (!selectedSubscription?.product?.fundCode) {
      message.warning("Please select a subscription first");
      return;
    }

    const companyPetraId = getCompanyPetraId(
      currentCustomer,
      selectedCompanyId || "",
    );

    if (!selectedProviderId) {
      message.warning("Please select a provider first");
      return;
    }

    if (!formData.productId) {
      message.warning("Please select a product first");
      return;
    }

    this.setState({
      isCheckingLoanBreakdown: true,
      loanBreakdownError: undefined,
    });

    try {
      const response: AxiosResponse = await request({
        query: CHECK_LOAN_BREAKDOWN,
        variables: {
          companyPetraId: companyPetraId || "",
          customerPetraId: currentCustomer.petraId,
          fundCode: selectedSubscription.product.fundCode,
          loanAmount: formData.loanAmountRequested,
          loanType: formData.loanType,
          portionOfContributionToBeUsed: formData.portionOfContributionToBeUsed,
          endDate: new Date().toISOString().split("T")[0],
          loanTenureMonths: parseInt(formData.loanTenureMonths),
          loanProductId: formData.productId,
        },
      });

      const result = response.data?.data
        ?.checkLoanBreakdown as LoanBreakdownData;

      if (!result) {
        message.error("Failed to get loan breakdown data");
        return;
      }

      if (result.success) {
        this.setState({ loanBreakdownData: result });

        if (result.data.eligible_for_loan) {
          message.success(
            `Maximum loanable amount: GHS ${result.data.loanable_amount.toLocaleString()}`,
          );
        } else {
          message.warning("Customer is not eligible for this loan amount");
        }
      } else {
        const errorMessage = result.error || "Failed to check loan breakdown";
        message.error(errorMessage);
        this.setState({
          loanBreakdownData: undefined,
          loanBreakdownError: errorMessage,
        });
      }
    } catch (error) {
      message.error(`Failed to check loan breakdown - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
      this.setState({
        loanBreakdownData: undefined,
        loanBreakdownError: "An error occurred while checking loan breakdown",
      });
    } finally {
      this.setState({ isCheckingLoanBreakdown: false });
    }
  };

  render() {
    const {
      isSubmitting,
      selectedCustomer,
      customers,
      isSearching,
      providers,
      products,
      loanBreakdownData,
      loanBreakdownError,
      isCheckingLoanBreakdown,
      salesPersons,
      createLoanError,
    } = this.state;

    const currentCustomer = selectedCustomer || this.props.customer;

    const fields: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder:
          "Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ...",
        rule: "string",
        label: "Customer",
        disabled: !!this.props.customer,
        initialValue: this.props.customer?.id,
        selectOptions: convertCustomersToSelectOptions(customers),
        changeHandler: (val: string) => this.handleCustomerSearch(val),
        selectHandler: (id: string) => this.handleCustomerSelect(id),
        isSearching,
        colConfig: { span: 24 },
      },
      {
        id: "companyId",
        name: "companyId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Company",
        label: "Select Company",
        selectOptions:
          currentCustomer?.customerCompanyAssociations?.map((association) => ({
            id: association.company.id,
            name: association.company.name,
            value: association.company.id,
          })) || [],
        selectHandler: (id: string) => this.handleCompanySelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "customerSubscriptionId",
        name: "customerSubscriptionId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Customer Subscription",
        label: "Select Customer Subscription",
        selectOptions:
          filterActiveSubscriptions(currentCustomer?.customerSubscriptions).map(
            (subscription) => ({
              id: subscription.id,
              name: subscription.product.name,
              value: subscription.id,
            }),
          ) || [],
        selectHandler: (id: string) => this.handleSubscriptionSelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "portionOfContributionToBeUsed",
        name: "portionOfContributionToBeUsed",
        inputType: "multipleSelect",
        rule: "array",
        required: true,
        placeholder: "Select Portion of Contribution To Be Used",
        label: "Portion of Contribution To Be Used",
        selectOptions: contributionTypes,
        colConfig: { span: 24 },
      },
      {
        id: "providerId",
        name: "providerId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Provider",
        label: "Provider",
        selectOptions: providers,
        colConfig: { span: 24 },
        selectHandler: (id: string) => this.handleProviderSelect(id),
      },
      {
        id: "productId",
        name: "productId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Product",
        label: "Product",
        selectOptions: products,
        selectHandler: (id: string) => this.handleProductSelect(id),
        colConfig: { span: 24 },
      },
      {
        id: "loanType",
        name: "loanType",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Loan Type",
        label: "Loan Type",
        selectOptions: loanTypes,
        selectHandler: (value: string) => this.handleLoanTypeSelect(value),
        colConfig: { span: 24 },
      },
      {
        id: "loanAmountRequested",
        name: "loanAmountRequested",
        inputType: "number",
        rule: "number",
        required: true,
        placeholder: "Enter loan amount",
        label: "Loan Amount Requested",
        colConfig: { span: 24 },
        elementStyle: { width: "100%" },
      },
      {
        id: "loanTenureMonths",
        name: "loanTenureMonths",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select loan tenure",
        label: "Loan Tenure (Months)",
        selectOptions: loanTenureMonths,
        colConfig: { span: 24 },
      },
      {
        id: "loanPurpose",
        name: "loanPurpose",
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Enter loan purpose",
        label: "Loan Purpose",
        colConfig: { span: 24 },
      },
      {
        id: "salesPersonId",
        name: "salesPersonId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Sales Person",
        placeholder: "Select sales person",
        selectOptions: salesPersons,
        changeHandler: this.handleSalesPersonSearchInput,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
    ];

    const requiredDocuments = this.state.requiredDocuments || [];

    const documentFields: FormFieldConfig[] = [
      {
        id: "ghanaCardFrontUrl",
        name: "ghanaCardFrontUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.ghanaCardFrontUrl,
        required: false,
        disabled: false,
        label: "Ghana Card (Front)",
        defaultFileList: this.state.ghanaCardFrontUrl
          ? [
            {
              uid: "1",
              name:
                this.state.ghanaCardFrontUrl.split("/").pop() ||
                "Ghana Card (Front)",
              status: "done",
              url: this.state.ghanaCardFrontUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("ghanaCardFrontUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "ghanaCardBackUrl",
        name: "ghanaCardBackUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.ghanaCardBackUrl,
        required: false,
        disabled: false,
        label: "Ghana Card (Back)",
        defaultFileList: this.state.ghanaCardBackUrl
          ? [
            {
              uid: "1",
              name:
                this.state.ghanaCardBackUrl.split("/").pop() ||
                "Ghana Card (Back)",
              status: "done",
              url: this.state.ghanaCardBackUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("ghanaCardBackUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "passportUrl",
        name: "passportUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.passportUrl,
        required: false,
        disabled: false,
        label: "Passport Picture",
        defaultFileList: this.state.passportUrl
          ? [
            {
              uid: "1",
              name: this.state.passportUrl.split("/").pop() || "Passport",
              status: "done",
              url: this.state.passportUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("passportUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: "image/*,.pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "applicationFormUrl",
        name: "applicationFormUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.applicationFormUrl,
        required: false,
        disabled: false,
        label: "Application Form",
        defaultFileList: this.state.applicationFormUrl
          ? [
            {
              uid: "1",
              name:
                this.state.applicationFormUrl.split("/").pop() ||
                "Application Form",
              status: "done",
              url: this.state.applicationFormUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("applicationFormUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMonthsPayslipUrl",
        name: "threeMonthsPayslipUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMonthsPayslipUrl,
        required: false,
        disabled: false,
        label: "3 Months Payslip",
        defaultFileList: this.state.threeMonthsPayslipUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMonthsPayslipUrl.split("/").pop() ||
                "3 Months Payslip",
              status: "done",
              url: this.state.threeMonthsPayslipUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("threeMonthsPayslipUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "standingOrderUrl",
        name: "standingOrderUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.standingOrderUrl,
        required: false,
        disabled: false,
        label: "Standing Order",
        defaultFileList: this.state.standingOrderUrl
          ? [
            {
              uid: "1",
              name:
                this.state.standingOrderUrl.split("/").pop() ||
                "Standing Order",
              status: "done",
              url: this.state.standingOrderUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("standingOrderUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMonthBankStatementUrl",
        name: "threeMonthBankStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMonthBankStatementUrl,
        required: false,
        disabled: false,
        label: "3 Month Bank Statement",
        defaultFileList: this.state.threeMonthBankStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMonthBankStatementUrl.split("/").pop() ||
                "3 Month Bank Statement",
              status: "done",
              url: this.state.threeMonthBankStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "threeMonthBankStatementUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "threeMomoStatementUrl",
        name: "threeMomoStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.threeMomoStatementUrl,
        required: false,
        disabled: false,
        label: "3 Month MoMo Statement",
        defaultFileList: this.state.threeMomoStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.threeMomoStatementUrl.split("/").pop() ||
                "3 Month MoMo Statement",
              status: "done",
              url: this.state.threeMomoStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("threeMomoStatementUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "oneYearConsistentTier2ContributionUrl",
        name: "oneYearConsistentTier2ContributionUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.oneYearConsistentTier2ContributionUrl,
        required: false,
        disabled: false,
        label: "1 Year Tier 2 Contribution",
        defaultFileList: this.state.oneYearConsistentTier2ContributionUrl
          ? [
            {
              uid: "1",
              name:
                this.state.oneYearConsistentTier2ContributionUrl
                  .split("/")
                  .pop() || "1 Year Tier 2 Contribution",
              status: "done",
              url: this.state.oneYearConsistentTier2ContributionUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "oneYearConsistentTier2ContributionUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "clearanceFormUrl",
        name: "clearanceFormUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.clearanceFormUrl,
        required: false,
        disabled: false,
        label: "Clearance Form",
        defaultFileList: this.state.clearanceFormUrl
          ? [
            {
              uid: "1",
              name:
                this.state.clearanceFormUrl.split("/").pop() ||
                "Clearance Form",
              status: "done",
              url: this.state.clearanceFormUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("clearanceFormUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "settlementQuotationUrl",
        name: "settlementQuotationUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.settlementQuotationUrl,
        required: false,
        disabled: false,
        label: "Settlement Quotation",
        defaultFileList: this.state.settlementQuotationUrl
          ? [
            {
              uid: "1",
              name:
                this.state.settlementQuotationUrl.split("/").pop() ||
                "Settlement Quotation",
              status: "done",
              url: this.state.settlementQuotationUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("settlementQuotationUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "sixMonthsBankStatementUrl",
        name: "sixMonthsBankStatementUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.sixMonthsBankStatementUrl,
        required: false,
        disabled: false,
        label: "6 Months Bank Statement",
        defaultFileList: this.state.sixMonthsBankStatementUrl
          ? [
            {
              uid: "1",
              name:
                this.state.sixMonthsBankStatementUrl.split("/").pop() ||
                "6 Months Bank Statement",
              status: "done",
              url: this.state.sixMonthsBankStatementUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange(
            "sixMonthsBankStatementUrl",
            combinedInfo,
          ),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "sixMonthsPayslipsUrl",
        name: "sixMonthsPayslipsUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.sixMonthsPayslipsUrl,
        required: false,
        disabled: false,
        label: "6 Months Payslips",
        defaultFileList: this.state.sixMonthsPayslipsUrl
          ? [
            {
              uid: "1",
              name:
                this.state.sixMonthsPayslipsUrl.split("/").pop() ||
                "6 Months Payslips",
              status: "done",
              url: this.state.sixMonthsPayslipsUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("sixMonthsPayslipsUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "employmentOfferLetterUrl",
        name: "employmentOfferLetterUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.employmentOfferLetterUrl,
        required: false,
        disabled: false,
        label: "Employment Offer Letter",
        defaultFileList: this.state.employmentOfferLetterUrl
          ? [
            {
              uid: "1",
              name:
                this.state.employmentOfferLetterUrl.split("/").pop() ||
                "Employment Offer Letter",
              status: "done",
              url: this.state.employmentOfferLetterUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("employmentOfferLetterUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "borrowersMandateUrl",
        name: "borrowersMandateUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.borrowersMandateUrl,
        required: false,
        disabled: false,
        label: "Borrower's Mandate",
        defaultFileList: this.state.borrowersMandateUrl
          ? [
            {
              uid: "1",
              name:
                this.state.borrowersMandateUrl.split("/").pop() ||
                "Borrower's Mandate",
              status: "done",
              url: this.state.borrowersMandateUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("borrowersMandateUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
      {
        id: "guarantorFormsUrl",
        name: "guarantorFormsUrl",
        inputType: "fileUpload",
        rule: "string",
        initialValue: this.state.guarantorFormsUrl,
        required: false,
        disabled: false,
        label: "Guarantor Forms",
        defaultFileList: this.state.guarantorFormsUrl
          ? [
            {
              uid: "1",
              name:
                this.state.guarantorFormsUrl.split("/").pop() ||
                "Guarantor Forms",
              status: "done",
              url: this.state.guarantorFormsUrl,
            },
          ]
          : [],
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("guarantorFormsUrl", combinedInfo),
        onFileDelete: this.handleDelete,
        fileUploadProps: {
          accept: ".pdf,application/pdf",
        },
        colConfig: { span: 10 },
      },
    ];

    const filteredDocumentFields =
      requiredDocuments.length > 0
        ? documentFields
          .filter((field) => requiredDocuments.includes(field.name as string))
          .map((field, index) => ({
            ...field,
            colConfig: {
              span: 10,
              offset: index % 2 === 1 ? 1 : 0,
            },
          }))
        : [];

    const fieldsToHide: string[] = [];
    const customer = this.state.selectedCustomer || this.props.customer;

    if (customer?.ghanaCardIdUrl && this.state.ghanaCardFrontUrl) {
      fieldsToHide.push("ghanaCardFrontUrl");
    }
    if (customer?.ghanaCardIdBackUrl && this.state.ghanaCardBackUrl) {
      fieldsToHide.push("ghanaCardBackUrl");
    }

    return (
      <Spin spinning={isSubmitting}>
        <Row>
          <Col span={8} style={{ margin: "0 20px" }}>
            <BaseForm
              layout="vertical"
              reference={this.formRef}
              fields={fields}
              fieldsToHide={[]}
            />
          </Col>

          <Col span={14}>
            {loanBreakdownData && (
              <LoanEligibilityCard loanBreakdownData={loanBreakdownData} />
            )}

            {loanBreakdownError && (
              <Alert
                message="Loan Breakdown Error"
                description={loanBreakdownError}
                type="error"
                showIcon
                closable
                style={{ marginBottom: 16 }}
                onClose={() => this.setState({ loanBreakdownError: undefined })}
              />
            )}

            <Collapse
              style={{ marginTop: 16 }}
              defaultActiveKey={["documents"]}
            >
              <Collapse.Panel header="Supporting Documents" key="documents">
                <BaseForm
                  key={`${this.state.ghanaCardFrontUrl}-${this.state.ghanaCardBackUrl}`}
                  layout="vertical"
                  reference={this.documentFormRef}
                  fields={filteredDocumentFields}
                  fieldsToHide={fieldsToHide}
                />
              </Collapse.Panel>
            </Collapse>

            <Row gutter={16} style={{ margin: "24px 0" }}>
              <Col span={8}>
                <Button
                  onClick={this.checkLoanBreakdown}
                  loading={isCheckingLoanBreakdown}
                  style={{ width: "100%" }}
                  disabled={isSubmitting}
                >
                  Check Loan Eligibility
                </Button>
              </Col>
              {loanBreakdownData?.data.eligible_for_loan && !createLoanError && (
                <Col span={8}>
                  <Button
                    type="primary"
                    onClick={this.createLoanRequest}
                    loading={isSubmitting}
                    style={{ width: "100%" }}
                  >
                    Create Loan
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Spin>
    );
  }
}
